import { useEffect, useState } from 'react';
import { noop } from 'lodash';

/**
 * Cross-component stagger queue. When multiple charts register simultaneously,
 * their mount callbacks are released one at a time via requestIdleCallback
 * (falling back to setTimeout). The first registration drains immediately —
 * only subsequent ones wait for the next idle slot.
 *
 * This prevents N parallel Highcharts SVG builds in a single task, which is the
 * root of the 400ms+ main-thread blocks observed on the APM service detail page.
 *
 * The hook is unconditional on purpose: every caller enters the queue on mount.
 * Visibility gating (e.g. "wait until scrolled into view before queueing") is
 * the caller's responsibility — only mount the component that calls this hook
 * once gating is satisfied. See `LazyGraph` for that layering.
 */

const STAGGER_INTERVAL_MS = 100;

type Callback = () => void;

const queue: Callback[] = [];
let draining = false;

const schedule: (cb: Callback) => void =
  typeof requestIdleCallback !== 'undefined'
    ? cb => {
        requestIdleCallback(() => cb(), { timeout: 200 });
      }
    : cb => {
        setTimeout(cb, STAGGER_INTERVAL_MS);
      };

const drain = (): void => {
  const next = queue.shift();

  if (!next) {
    draining = false;

    return;
  }

  try {
    next();
  } catch (error) {
    noop();
  }

  if (queue.length > 0) {
    schedule(drain);
  } else {
    draining = false;
  }
};

export const enqueue = (cb: Callback): (() => void) => {
  queue.push(cb);

  if (!draining) {
    draining = true;
    // First consumer is released in the next microtask so a single chart does not wait.
    Promise.resolve().then(drain);
  }

  return () => {
    const index = queue.indexOf(cb);

    if (index >= 0) {
      queue.splice(index, 1);
    }
  };
};

export const useStaggeredMount = (): { isReady: boolean } => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (isReady) return;

    let cancelled = false;
    const cancel = enqueue(() => {
      if (!cancelled) setIsReady(true);
    });

    return () => {
      cancelled = true;
      cancel();
    };
  }, [isReady]);

  return { isReady };
};
